/* eslint-disable @typescript-eslint/no-explicit-any */
import { findAndReplace } from "mdast-util-find-and-replace";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

/** Obsidian extensions on top of remark-gfm + remark-math.
 *
 * remark-gfm already covers tables, task lists, strikethrough (~~), footnotes,
 * and autolinks; remark-math + rehype-katex cover $inline$ and $$block$$ math.
 * These two small transforms add the pieces GFM doesn't have. They run on text
 * nodes only and `findAndReplace` skips code/inlineCode, so fenced + inline code
 * is never rewritten (a `==x==` inside a code span stays literal). */

/** `==highlight==` → a <mark>. The matched text is emitted as a node whose
 * `data.hName` overrides the rendered tag, so remark-rehype produces <mark>.
 *
 * A `==` delimiter may hug whitespace (`== spaced ==` highlights) — the pattern
 * is kept in step with the editor's `Highlight` Lezer extension, which relaxed
 * the same rule so the effect does not flicker off the moment a space is typed.
 * `(?!=)` keeps a bare `====` literal: an EMPTY marker pair renders as itself
 * rather than silently collapsing to nothing. */
export function remarkHighlight() {
  return (tree: any) => {
    findAndReplace(tree, [
      [
        /==(?!=)([\s\S]+?)==/g,
        (_m: string, inner: string) => ({
          type: "strong",
          data: { hName: "mark" },
          children: [{ type: "text", value: inner }],
        }),
      ],
    ]);
  };
}

/** Whitespace-tolerant `**bold**` / `__bold__` / `~~struck~~`, matching the
 * editor's `relaxedPair` Lezer extension so Live and Preview agree.
 *
 * This runs as a MOP-UP, not a replacement: remark has already turned every
 * CommonMark-legal `**tight**` into a `strong` node, so what reaches these
 * patterns as literal text is exactly the space-hugging forms CommonMark
 * rejects. `findAndReplace` only visits text nodes and skips code, so a marker
 * inside a code span stays literal either way.
 *
 * Single `*` and `_` are deliberately absent: relaxing those is what would turn
 * `2 * 3 * 4` into italics. */
export function remarkRelaxedEmphasis() {
  return (tree: any) => {
    // ONLY the space-hugging forms. Everything CommonMark accepts has already
    // become a node, so a TIGHT `**…**` still sitting here as literal text got
    // here by being escaped (`\*\*`) — remark ate the backslashes, and matching
    // it now would silently un-escape what the writer explicitly escaped.
    // Requiring an adjacent space is what separates the two cases.
    const wrap = (type: string) => (_m: string, inner: string): any =>
      /^\s|\s$/.test(inner) ? { type, children: [{ type: "text", value: inner }] } : false;
    findAndReplace(tree, [
      // `(?!\*)` / `(?!_)` / `(?!~)` leave a longer run (`***both***`) to remark.
      [/\*\*(?!\*)([\s\S]+?)\*\*/g, wrap("strong")],
      [/__(?!_)([\s\S]+?)__/g, wrap("strong")],
      [/~~(?!~)([\s\S]+?)~~/g, wrap("delete")],
    ] as any);
  };
}

/** The markdown dialect GraphView renders, as a plugin pipeline — GFM + math +
 * the two Obsidian-flavoured transforms above. Shared so every surface that
 * compiles markdown parses it IDENTICALLY: the in-app `<Markdown>` (which adds
 * `remarkWikiLink`, since only it can resolve a wikilink to a node) and the
 * public reading page (which cannot, and so leaves it out). Anything that
 * changes how source turns into a document belongs here, not at a call site. */
export const MARKDOWN_REMARK_PLUGINS = [
  remarkGfm,
  remarkMath,
  remarkHighlight,
  remarkRelaxedEmphasis,
];

/** The rehype half of the same pipeline: `$math$` → rendered KaTeX. */
export const MARKDOWN_REHYPE_PLUGINS = [rehypeKatex];

/** `[[Target]]` and `[[Target|alias]]` → an internal link with the href scheme
 * `#wiki:<target>`. The Markdown component's `a` renderer resolves that scheme
 * to a node on click (documents open in the reader; everything else focuses in
 * the atlas), matching the backend's `[[wikilink]]` resolution. */
export function remarkWikiLink() {
  return (tree: any) => {
    findAndReplace(tree, [
      [
        /\[\[([^\]|\n]+?)(?:\|([^\]\n]+?))?\]\]/g,
        (_m: string, target: string, alias?: string) => ({
          type: "link",
          url: `#wiki:${encodeURIComponent(target.trim())}`,
          data: { hProperties: { className: "wikilink" } },
          children: [{ type: "text", value: (alias ?? target).trim() }],
        }),
      ],
    ]);
  };
}
